import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  startTransition,
  useMemo,
} from "react"
import Link from "../../link"
import CategoryCard from "../../cards/category-card"
import cn from "../../../utils/classname"
import ButtonLink from "../../../ui/button-link"

type Option = {
  path: string
  title: string
  external?: boolean
}
const JOBS_URL = process.env.JOBS_URL || "https://jobs.sitepoint.com"
export default function Home({ topics }) {
  const [currentLine, setCurrentLine] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  const optionsRef = useRef<(HTMLAnchorElement | null)[]>([])

  const options: Option[] = useMemo(
    () => [
      {
        path: "/premium/pricing/",
        title: "Start free trial",
        external: true,
      },
      {
        path: "/premium/library/",
        title: "Browse library",
        external: true,
      },
      {
        path: `${JOBS_URL}/`,
        title: "Find a developer job",
        external: true,
      },
      {
        path: "/community/",
        title: "Join a community",
        external: true,
      },
      {
        path: "/#newsletters",
        title: "Read our developer newsletter",
      },
    ],
    []
  )

  const handleArrowUp = useCallback(() => {
    if (currentLine > 0) {
      startTransition(() => {
        setCurrentLine((prev) => prev - 1)
      })
    }
  }, [currentLine])

  const handleArrowDown = useCallback(() => {
    if (currentLine < options.length - 1) {
      startTransition(() => {
        setCurrentLine((prev) => prev + 1)
      })
    }
  }, [currentLine, options.length])

  const handleEnter = useCallback(() => {
    optionsRef.current[currentLine]?.click()
  }, [currentLine])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (["ArrowUp", "ArrowDown", "Enter"].includes(e.key)) {
        if (
          (e.key === "ArrowUp" && currentLine > 0) ||
          (e.key === "ArrowDown" && currentLine < options.length - 1) ||
          e.key === "Enter"
        ) {
          e.preventDefault() // Prevent default behavior only when necessary
        }
        switch (e.key) {
          case "ArrowUp":
            handleArrowUp()
            break
          case "ArrowDown":
            handleArrowDown()
            break
          case "Enter":
            handleEnter()
            break
          default:
            break
        }
      }
    },
    [handleArrowUp, handleArrowDown, handleEnter, currentLine, options]
  )

  useEffect(() => {
    const container = containerRef.current
    if (container) {
      container.addEventListener("keydown", handleKeyDown)
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          container?.focus() // Focus the container when it comes into view
        }
      },
      { threshold: 1 } // Adjust the threshold as needed
    )

    if (container) {
      observer.observe(container)
    }

    return () => {
      if (container) {
        container.removeEventListener("keydown", handleKeyDown)
        observer.unobserve(container)
      }
    }
  }, [handleKeyDown])

  return (
    <>
      <div className="flex justify-center items-center flex-col container-lg lg:mt-10">
        <div
          ref={containerRef}
          tabIndex={0}
          className="w-[calc(100%+32px)] -mx-4 xl:mx-0  xl:w-full bg-background-1 xl:rounded-2xl hero-terminal-shadow overflow-hidden pb-14 outline-none"
        >
          <div className="bg-background-1 px-6 py-5 flex items-center relative">
            <div className="flex space-x-2">
              <div className="w-3 h-3 bg-[#ED6A5F] rounded-full"></div>
              <div className="w-3 h-3 bg-[#F5BF4F] rounded-full"></div>
              <div className="w-3 h-3 bg-[#61C554] rounded-full"></div>
            </div>
            <div className="text-center text-gray-400 text-2 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              sitepoint-terminal
            </div>
          </div>
          <div className="font-mono-display text-sm text-neutral-11">
            <pre className="font-mono-display hidden px-4 sm:px-12 sm:block pt-7 pb-0 text-white text-jar-4 bg-background-1">
              {`
   _____ _ _       _____      _       _   
  / ____(_) |     |  __ \\    (_)     | |  
 | (___  _| |_ ___| |__) |__  _ _ __ | |_ 
  \\___ \\| | __/ _ \\  ___/ _ \\| | '_ \\| __|
  ____) | | ||  __/ |  | (_) | | | | | |_ 
 |_____/|_|\\__\\___|_|   \\___/|_|_| |_|\\__|

`}
            </pre>
            <div className="px-4 sm:px-12 text-white text-jar-5 mt-7 font-mono-display">
              <div className="flex flex-row gap-5">
                <span>#</span>
                <p>Welcome to SitePoint!</p>
              </div>
              <div className="flex flex-row gap-5">
                <span>#</span>
                <p>Access a huge library of books and courses</p>
              </div>
              <div className="flex flex-row gap-5">
                <span>#</span>
                <p>On various topics: JavaScript, Python, AI, and more!</p>
              </div>
              <div className="hidden sm:flex flex-row gap-5 mt-7">
                <span>#</span>
                <p>Use arrows to navigate</p>
              </div>
            </div>
            <div className="mt-4 text-jar-5 font-mono-display">
              {options.map((option, index) => (
                <Link
                  to={option.path}
                  key={index}
                  ref={(el) => (optionsRef.current[index] = el)}
                  className={cn(
                    "flex px-4 sm:px-12 py-2 text-[#b2b3bd] cursor-pointer hover:bg-[#222225] hover:text-white items-center gap-5 group",
                    currentLine === index && "bg-[#222225] text-white"
                  )}
                  tabIndex={currentLine === index ? 0 : -1}
                  onMouseEnter={() => setCurrentLine(index)}
                  external={option.external}
                >
                  <span
                    className={cn(
                      "text-[#b2b3bd] block group-hover:hidden",
                      currentLine === index && "hidden"
                    )}
                  >
                    {index + 1}
                  </span>
                  <span
                    className={cn(
                      "text-white hidden group-hover:block",
                      currentLine === index && "block"
                    )}
                  >
                    {">"}
                  </span>
                  <span>{option.title}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-16 sm:mt-[120px] w-[calc(100%+32px)] lg:w-full overflow-hidden">
          <div className="flex flex-row justify-between items-center px-4 lg:px-0">
            <div className="text-jar-6 font-semibold text-neutral-12">
              Top categories
            </div>
            <ButtonLink
              href="/premium/library/"
              size={2}
              theme={"neutral"}
              underline
              contrast={"high"}
              external
            >
              Browse all
            </ButtonLink>
          </div>
          <div
            className={cn(
              "mt-8 sm:mt-10 overflow-x-auto snap-x snap-mandatory",
              "pb-5 lg:pb-0"
            )}
          >
            <div className="grid grid-cols-4 gap-y-2 sm:gap-y-6 gap-x-3 sm:gap-6  px-4 lg:px-0 w-[1380px] lg:w-full ">
              {topics?.map(({ name, slug, contentCount }) => (
                <CategoryCard
                  key={slug}
                  info={`${contentCount} courses`}
                  name={name}
                  slug={slug}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
